import { DormantClients } from '../components/DormantClients';
import { CsvExportButton, useCsvExport } from '../components/CsvExport';
// Clients per mockups/05-clients.html: vitals strip, "Active now" group
// (clients with live projects, showing the project + phase), A–Z directory
// with recent-sort toggle and show-all, All/Active/Past tabs, and the
// new-client modal (09-new-client, 520px).

import { useViewState } from '../lib/useViewState';
import { oneOf, str, nullableStr, bool, intMin } from '../lib/viewState';
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { melbourneToday } from '../../shared/dates';
import { CLIENT_STATUSES, PHASE_PILL_LABELS, type ClientWithCounts, type ProjectPhase } from '../../shared/types';
import {
  FilterBar,
  Modal,
  PageHeader,
  Pill,
  SearchBox,
  SkeletonRows,
  Tabs,
  Vital,
  Vitals,
} from '../components/primitives';
import { FilterSheet } from '../components/FilterSheet';
import { DotsIcon, FilterIcon, SearchIcon } from '../components/icons';
import { PageMenu } from '../components/PageMenu';
import {
  clientActiveMeta,
  clientDirectoryMeta,
  clientRightMeta,
  clientVitals,
  filterClients,
  monthYear,
  projectsLabel,
} from '../lib/clientListMeta';
import { useQuickAddAction } from '../lib/quickAdd';
import { useIsMobile } from '../lib/useIsMobile';
import { ViewChips } from '../components/ViewChips';

const CLIENT_STATUS_LABELS: Record<(typeof CLIENT_STATUSES)[number], string> = {
  lead: 'Lead',
  active: 'Active',
  past: 'Past',
  archived: 'Archived',
};

interface DirectoryClient extends ClientWithCounts {
  first_project_date: string | null;
  last_project_date: string | null;
  active_project_name: string | null;
  active_project_phase: ProjectPhase | null;
}

type View = 'all' | 'active' | 'past' | 'dormant';

const PAGE = 30;

export const clientsViewCodec = {
  view: oneOf('view', ['all', 'active', 'past', 'dormant'], 'all'), query: str('q'),
  status: nullableStr('status'), sortRecent: bool('recent'), shown: intMin('shown', PAGE, PAGE),
};

export default function Clients() {
  const { initial, publish } = useViewState(clientsViewCodec, state => {
    setView(state.view);
    setQuery(state.query);
    setFilters({ status: state.status });
    setSortRecent(state.sortRecent);
    setShown(state.shown);
  });
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [draftStatus, setDraftStatus] = useState<string | null>(null);
  const [draftQuery, setDraftQuery] = useState('');
  const [clients, setClients] = useState<DirectoryClient[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [view, setView] = useState<View>(() => initial.view);
  const [query, setQuery] = useState(() => initial.query);
  const [filters, setFilters] = useState<Record<string, string | null>>(() => ({ status: initial.status }));
  const [sortRecent, setSortRecent] = useState(() => initial.sortRecent);
  const [shown, setShown] = useState(() => initial.shown);

  useEffect(() => {
    publish({ view, query, status: filters.status, sortRecent, shown });
  }, [publish, view, query, filters.status, sortRecent, shown]);
  const [showNew, setShowNew] = useState(false);

  useQuickAddAction({ label: 'New client', onSelect: () => setShowNew(true) });

  // Status filter + saved views (Phase 1.12). The filter reaches 'lead' /
  // 'archived', which the All/Active/Past tabs don't expose on their own.
  const filterFields = [
    {
      key: 'status',
      label: 'Status',
      items: CLIENT_STATUSES.map((s) => ({ value: s, label: CLIENT_STATUS_LABELS[s] })),
    },
  ];
  const viewState = { view, query, ...filters };
  const isDefaultView = view === 'all' && !query && !filters.status;
  const applyView = (p: Record<string, string>) => {
    setView((p.view as View) || 'all');
    setQuery(p.query ?? '');
    setFilters({ status: p.status ?? null });
  };
  const navigate = useNavigate();
  const today = melbourneToday();

  const listSeq = useRef(0);
  const fetchClients = useCallback(() => {
    const seq = ++listSeq.current;
    api<{ clients: DirectoryClient[] }>('/api/admin/clients')
      .then(d => { if (seq === listSeq.current) setClients(d.clients); })
      .catch(() => {})
      .finally(() => { if (seq === listSeq.current) setLoaded(true); });
  }, []);
  const mutatedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refresh = useCallback(() => {
    if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
    mutatedTimer.current = null;
    fetchClients();
  }, [fetchClients]);
  useEffect(() => {
    fetchClients();
    const onMutated = () => {
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      mutatedTimer.current = setTimeout(fetchClients, 250);
    };
    window.addEventListener('ss:mutated', onMutated);
    return () => {
      window.removeEventListener('ss:mutated', onMutated);
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      listSeq.current++;
    };
  }, [fetchClients]);

  const filtered = useMemo(
    () => filterClients(clients, { view: view==='dormant'?'all':view, status: filters.status, query }),
    [clients, view, query, filters],
  );

  const csvExport = useCsvExport('clients', { view, query, status: filters.status, recent: sortRecent }, { count: filtered.length });
  const activeNow = filtered.filter((c) => c.projects_active > 0);
  const directory = useMemo(() => {
    if (!sortRecent) return filtered; // API order is A–Z already
    return [...filtered].sort((a, b) =>
      (b.last_project_date ?? '') < (a.last_project_date ?? '') ? -1 : 1,
    );
  }, [filtered, sortRecent]);

  // Vitals over all clients (not the search subset).
  const fyStart =
    today >= `${today.slice(0, 4)}-07-01`
      ? `${today.slice(0, 4)}-07-01`
      : `${Number(today.slice(0, 4)) - 1}-07-01`;
  const activeNowAll = clients.filter((c) => c.projects_active > 0).length;
  const newFY = clients.filter((c) => c.first_project_date && c.first_project_date >= fyStart).length;
  const repeat = clients.filter((c) => c.projects_total >= 2).length;

  const row = (c: DirectoryClient, showActive: boolean) => (
    <div className="row" key={c.id} onClick={() => navigate(`/clients/${c.id}`)}>
      <span className="client-mark">{c.name.slice(0, 1).toUpperCase()}</span>
      <span className="row-title" title={c.name}>{c.name}</span>
      {(c.contact_name ?? c.contact_email) && (
        <span className="row-context" title={[c.contact_name, c.contact_email].filter(Boolean).join(' · ')}>
          {[c.contact_name, c.contact_email].filter(Boolean).join(' · ')}
        </span>
      )}
      <span className="row-spacer" />
      {showActive && c.active_project_name && c.active_project_phase && (
        <span className="active-pill">
          {c.active_project_name} · {PHASE_PILL_LABELS[c.active_project_phase]}
        </span>
      )}
      <span className="row-meta dim col-projects">{projectsLabel(c.projects_total)}</span>
      <span className="row-meta dim col-last">{monthYear(c.last_project_date)}</span>
    </div>
  );

  // ── ≤880px (Phase C §7.1 A) ────────────────────────────────────────────
  // Chips + a mono vitals line replace the toolbar and the four Vital cards;
  // the four-column desktop row becomes mark · name/meta · right stack.
  if (isMobile) {
    const vitals = clientVitals(activeNowAll, newFY, repeat);
    const activeTabCount = clients.filter((c) => c.status === 'active').length;
    const draftCount = filterClients(clients, { view: view==='dormant'?'all':view, status: draftStatus, query: draftQuery }).length;

    const mobileRow = (c: DirectoryClient, activeNowRow: boolean) => {
      const live = clientActiveMeta(c);
      const right = clientRightMeta(c, activeNowRow);
      return (
        <button type="button" className="client-row" key={c.id} onClick={() => navigate(`/clients/${c.id}`)}>
          <span className="client-mark">{c.name.slice(0, 1).toUpperCase()}</span>
          <span className="client-col">
            <span className="client-row-title">{c.name}</span>
            <span className="client-row-meta">
              {activeNowRow ? (
                <>
                  <span className="client-row-live">{live.project}</span>
                  {live.phase && (
                    <>
                      <span className="client-sep">·</span>
                      <Pill tone="accent">{live.phase}</Pill>
                    </>
                  )}
                </>
              ) : (
                <span className="client-row-live">{clientDirectoryMeta(c)}</span>
              )}
            </span>
          </span>
          <span className="client-right">
            <span>{right.projects}</span>
            {right.last && <span>{right.last}</span>}
          </span>
        </button>
      );
    };

    return (
      <>
        <PageHeader
          title="Clients"
          count={clients.length}
          mobileActions={[
            {
              key: 'search',
              label: 'Search',
              icon: <SearchIcon />,
              onPress: () => window.dispatchEvent(new Event('ss:open-search')),
            },
            {
              key: 'filter',
              label: 'Filter',
              icon: <FilterIcon />,
              active: !isDefaultView,
              onPress: () => {
                setDraftStatus(filters.status);
                setDraftQuery(query);
                setFilterOpen(true);
              },
            },
            { key: 'menu', label: 'More client actions', icon: <DotsIcon />, onPress: () => setMenuOpen(true) },
          ]}
        />

        <div className="mtabs">
          {([
            { value: 'all', label: 'All' },
            { value: 'active', label: 'Active', count: activeTabCount },
            { value: 'past', label: 'Past' },
            { value: 'dormant', label: 'Dormant' },
          ] as const).map((tab) => (
            <button
              key={tab.value}
              className={`t${view === tab.value ? ' on' : ''}`}
              onClick={() => {
                setView(tab.value);
                setShown(PAGE);
              }}
            >
              {tab.label}
              {'count' in tab && <span className="c">{tab.count}</span>}
            </button>
          ))}
        </div>
        {/* A saved view can carry a search; there is no SearchBox at this
            width, so the query shows as a chip that clears it (review C-1). */}
        {query.trim() !== '' && (
          <div className="chiprow client-query-chip">
            <button type="button" className="c on" onClick={() => setQuery('')} aria-label={`Clear search “${query}”`}>
              Search: {query} ×
            </button>
          </div>
        )}
        <ViewChips page="clients" current={viewState} isDefault={isDefaultView} onApply={applyView} />
        <div className="client-list-vitals">
          <span className="accent">{vitals.left}</span>
          <span className="gold">{vitals.right}</span>
        </div>

        <div className="page-body">
          <div className="content wide client-list-m">
            {view==='dormant'?<DormantClients query={query} status={filters.status??null}/>:<>
            {activeNow.length > 0 && (
              <>
                <div className="client-group">
                  <span>Active now</span>
                  <span className="n">{activeNow.length}</span>
                </div>
                {activeNow.map((c) => mobileRow(c, true))}
              </>
            )}

            <div className="client-group">
              <span>All clients</span>
              <span className="n">{sortRecent ? 'Recent' : 'A → Z'}</span>
              <button className="client-group-link" onClick={() => setSortRecent((sr) => !sr)}>
                Sort: {sortRecent ? 'A → Z' : 'Recent'} →
              </button>
            </div>
            {!loaded ? (
              <SkeletonRows />
            ) : (
              <>
                {directory.slice(0, shown).map((c) => mobileRow(c, false))}
                {directory.length > shown && (
                  <button className="show-more" onClick={() => setShown((n) => n + PAGE)}>
                    Show all {directory.length} clients →
                  </button>
                )}
                {directory.length === 0 && <div className="empty-note">No clients match.</div>}
              </>
            )}
            </>}
          </div>
        </div>

        <PageMenu
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          title="Clients"
          items={[{ key: 'new', label: 'New client', onSelect: () => setShowNew(true) }, ...(view==='dormant'?[]:[csvExport.item])]}
        />
        <FilterSheet
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          sections={[
            {
              key: 'status',
              label: 'Status',
              kind: 'chips',
              options: [{ value: '', label: 'Any' }, ...filterFields[0].items],
              value: draftStatus ?? '',
              onChange: (v: string) => setDraftStatus(v || null),
            },
          ]}
          resultLabel={view==='dormant'?'Apply filters':`Show ${draftCount} ${draftCount === 1 ? 'client' : 'clients'}`}
          onClear={() => {
            setDraftStatus(null);
            setDraftQuery('');
          }}
          onApply={() => {
            setFilters({ status: draftStatus });
            setQuery(draftQuery);
            setShown(PAGE);
            setFilterOpen(false);
          }}
        />
        {showNew && <NewClientModal onClose={() => setShowNew(false)} onCreated={refresh} />}
      </>
    );
  }

  return (
    <>
      <PageHeader title="Clients" count={clients.length} />

      <div className="toolbar">
        <Tabs
          options={[
            { value: 'all', label: 'All' },
            { value: 'active', label: 'Active' },
            { value: 'past', label: 'Past' },
            { value: 'dormant', label: 'Dormant' },
          ]}
          value={view}
          onChange={setView}
        />
        <FilterBar
          fields={filterFields}
          active={filters}
          onChange={(key, value) => setFilters((f) => ({ ...f, [key]: value }))}
        />
        <SearchBox placeholder={view==='dormant'?'Search clients, last projects…':'Search clients, contacts, emails…'} value={query} onChange={setQuery} />
        {view!=='dormant'&&<CsvExportButton state={csvExport} />}
        <button className="btn primary" onClick={() => setShowNew(true)}>
          + New client
        </button>
      </div>

      <ViewChips page="clients" current={viewState} isDefault={isDefaultView} onApply={applyView} />

      <div className="page-body">
        <div className="content wide" style={{ paddingTop: 4 }}>
          <Vitals>
            <Vital label="Clients" value={clients.length} sub="all time" />
            <Vital label="Active now" value={activeNowAll} tone="accent" sub="with live projects" />
            <Vital label="New this FY" value={newFY} sub="first project booked" />
            <Vital label="Repeat clients" value={repeat} tone="gold" sub="2+ projects" />
          </Vitals>

          {view==='dormant'?<DormantClients query={query} status={filters.status??null}/>:<>
          {activeNow.length > 0 && (
            <div className="group">
              <div className="group-head">
                <span className="group-title">Active now</span>
                <span className="group-count">{activeNow.length}</span>
              </div>
              {activeNow.map((c) => row(c, true))}
            </div>
          )}

          <div className="group">
            <div className="group-head">
              <span className="group-title">All clients</span>
              <span className="group-count">{sortRecent ? 'Recent' : 'A → Z'}</span>
              <a
                className="group-link"
                style={{ cursor: 'pointer' }}
                onClick={() => setSortRecent((s) => !s)}
              >
                Sort: {sortRecent ? 'A → Z' : 'recent'} →
              </a>
            </div>
            {!loaded ? (
              <SkeletonRows />
            ) : (
              <>
                {directory.slice(0, shown).map((c) => row(c, false))}
                {directory.length > shown && (
                  <button className="show-more" onClick={() => setShown((n) => n + PAGE)}>
                    Show all {directory.length} clients →
                  </button>
                )}
                {directory.length === 0 && <div className="empty-note">No clients match.</div>}
              </>
            )}
          </div>
          </>}
        </div>
      </div>

      {showNew && <NewClientModal onClose={() => setShowNew(false)} onCreated={refresh} />}
    </>
  );
}

// ── New client modal (09-new-client, 520px) ──────────────────────────────

function NewClientModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const isMobile = useIsMobile();
  const [form, setForm] = useState({
    name: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!form.name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api('/api/admin/clients', {
        method: 'POST',
        json: {
          name: form.name.trim(),
          contact_name: form.contact_name || null,
          contact_phone: form.contact_phone || null,
          contact_email: form.contact_email || null,
          notes: form.notes || null,
          status: 'active',
        },
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create client.');
      setBusy(false);
    }
  };

  return (
    <Modal
      mobile="full"
      title="New client"
      context="CLIENTS"
      width={520}
      onClose={onClose}
      hint="Dashboard can be added later"
      foot={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => submit()} disabled={!form.name.trim() || busy}>
            Create client →
          </button>
        </>
      }
    >
      <form className={isMobile ? "form-grid client-new-form" : "form-grid"} onSubmit={submit}>
        <div className="f-field span-2">
          <span className="f-label">
            Client name <span className="req">*</span>
          </span>
          <input className="f-input" value={form.name} onChange={set('name')} autoFocus />
        </div>
        <div className="f-field">
          <span className="f-label">Contact name</span>
          <input className="f-input" value={form.contact_name} onChange={set('contact_name')} placeholder="Primary contact" />
        </div>
        <div className={isMobile ? "f-field client-pair" : "f-field"}>
          <span className="f-label">Phone</span>
          <input className="f-input mono" type={isMobile ? "tel" : undefined} value={form.contact_phone} onChange={set('contact_phone')} placeholder="04…" />
        </div>
        <div className={isMobile ? "f-field client-pair" : "f-field span-2"}>
          <span className="f-label">Email</span>
          <input className="f-input mono" type={isMobile ? "email" : undefined} value={form.contact_email} onChange={set('contact_email')} placeholder="name@company.com.au" />
        </div>
        <div className="f-field span-2">
          <span className="f-label">Notes</span>
          <textarea
            className="f-textarea"
            value={form.notes}
            onChange={set('notes')}
            placeholder="How they found you, preferences, billing quirks…"
          />
        </div>
        {error && <p className="error-text span-2">{error}</p>}
      </form>
    </Modal>
  );
}
